import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Users, Settings, MapPin, FileText, Calendar, Eye, EyeOff, CreditCard, ShieldCheck, ExternalLink, Link2, Check } from 'lucide-react';
import { useCompany } from '../../hooks/useCompany';
import { gatewaysService } from '../../services/api';
import CompanySettingsModal from './settings/CompanySettingsModal';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';
import { getBusinessTypeLabel } from '../../constants/businessTypes';
import {
  MERCHANT_ALIAS_ANCHOR_ID,
  isMerchantAliasDeepLink
} from '../../utils/merchantAliasDeepLink';

/**
 * @description Company Overview tab with the Company Settings modal
 *
 * Key features:
 * - Company Settings modal (company info + locations)
 * - Add Alias deep-link (`#merchant-alias`) opens that modal and scrolls to Merchant Alias
 * - EIN visibility toggle
 *
 * @dependencies
 * - useCompany, CompanySettingsModal, merchantAliasDeepLink
 *
 * @notes
 * - Do not route Add Alias to a standalone /merchant/company/settings page
 */
const CompanyDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { company, loading, error, loadCompany, updateCompany } = useCompany();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(() =>
    isMerchantAliasDeepLink(location.hash)
  );
  const [updateError, setUpdateError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showEin, setShowEin] = useState(false);
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    // Load full company data if we don't have it yet
    if (!company) {
      loadCompany();
    }
  }, [company, loadCompany]);

  // Fetch Elavon merchant info (only when company is loaded)
  useEffect(() => {
    if (!company) return;

    const fetchMerchantInfo = async () => {
      try {
        const info = await gatewaysService.getMerchantInfo();
        setMerchantInfo(info);
      } catch {
        // No gateway configured or not Elavon — section simply won't render
        setMerchantInfo(null);
      }
    };

    fetchMerchantInfo();
  }, [company]);

  useEffect(() => {
    if (isMerchantAliasDeepLink(location.hash)) {
      setIsSettingsModalOpen(true);
      setUpdateError(null);
    }
  }, [location.hash]);

  const clearMerchantAliasHash = () => {
    if (!isMerchantAliasDeepLink(location.hash)) return;
    navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
  };

  const EMPLOYEE_PORTAL_FALLBACK_URL = process.env.REACT_APP_TIME_CLOCK_URL || 'https://portal.lynxpay.us';

  const copyPortalLink = async () => {
    if (!company?.id) return;
    const portalUrl = `${company?.employeePortalUrl || EMPLOYEE_PORTAL_FALLBACK_URL}/${company.id}`;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Open Company Settings modal
  const handleOpenSettings = () => {
    setIsSettingsModalOpen(true);
    setUpdateError(null);
  };

  const handleCloseSettings = () => {
    setIsSettingsModalOpen(false);
    setUpdateError(null);
    clearMerchantAliasHash();
  };

  const handleUpdateCompany = async (companyData) => {
    setIsUpdating(true);
    setUpdateError(null);

    const result = await updateCompany(companyData);

    if (result?.success) {
      // Close modal immediately on success - no delay needed
      setIsSettingsModalOpen(false);
      clearMerchantAliasHash();
    } else {
      setUpdateError(result?.error || 'Failed to update company');
    }

    setIsUpdating(false);
    return result;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
            <Building2 className="h-6 w-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-600 font-medium">Loading your company...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Building2 className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Company</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => loadCompany()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header Card - Enhanced with better mobile layout */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 sm:p-4 shadow-md flex-shrink-0">
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                    {company?.businessName || 'Company'}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {company?.businessType && (
                      <span className="text-sm text-gray-600">
                        {getBusinessTypeLabel(company.businessType)}
                      </span>
                    )}
                    {company?.city && company?.state && (
                      <>
                        {company?.businessType && <span className="text-gray-400">•</span>}
                        <span className="text-sm text-gray-600">
                          {company.city}, {company.state}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <PermissionGate permission={PERMISSIONS.settings.edit}>
                  <button
                    onClick={handleOpenSettings}
                    className="flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 w-full"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Company Settings
                  </button>
                </PermissionGate>
                <button
                  onClick={copyPortalLink}
                  className="flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 w-full"
                >
                  {linkCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-green-500" />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Employee Portal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Stats Grid - Enhanced for mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Total Employees */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all duration-200">
              <div className="flex items-center">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 sm:p-3.5 mr-3 sm:mr-4 shadow-sm flex-shrink-0">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Employees</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                    {company?.employeeStats?.total || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Active Employees */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all duration-200">
              <div className="flex items-center">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 sm:p-3.5 mr-3 sm:mr-4 shadow-sm flex-shrink-0">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Active Employees</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-0.5 sm:mt-1">
                    {company?.employeeStats?.active || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Teams */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all duration-200 opacity-90">
              <div className="flex items-center">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 sm:p-3.5 mr-3 sm:mr-4 shadow-sm flex-shrink-0">
                  <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Teams</p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-0.5 sm:mt-1">0</p>
                  <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">Coming soon</p>
                </div>
              </div>
            </div>

            {/* Accounting Method */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all duration-200">
              <div className="flex items-center">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-3 sm:p-3.5 mr-3 sm:mr-4 shadow-sm flex-shrink-0">
                  <Settings className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Accounting Method</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 capitalize mt-0.5 sm:mt-1 truncate">
                    {company?.accountingMethod || 'Not set'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Information - Enhanced with icons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {/* Business Address */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-600">Business Address</p>
                </div>
                <div className="pl-6 text-gray-900 leading-relaxed">
                  {company?.addressLine1 ? (
                    <>
                      <p>{company.addressLine1}</p>
                      {company?.addressLine2 && <p>{company.addressLine2}</p>}
                      <p>{company?.city}, {company?.state} {company?.postalCode}</p>
                    </>
                  ) : (
                    <p className="text-gray-500 italic">Not provided</p>
                  )}
                </div>
              </div>

              {/* Tax Information */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-600">Tax Information</p>
                </div>
                <div className="pl-6 text-gray-900 leading-relaxed space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                      {company?.ein ? (
                        <button
                          onClick={() => setShowEin(!showEin)}
                          className="text-gray-400 hover:text-gray-600 transition-colors rounded hover:bg-gray-100"
                          type="button"
                          aria-label={showEin ? 'Hide EIN' : 'Show EIN'}
                        >
                          {showEin ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <p>
                      <span className="font-medium">EIN:</span>{' '}
                      {company?.ein ? (
                        showEin ? company.ein : `${company.ein.slice(0, 2)}-*******`
                      ) : 'Not provided'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-3 w-3 text-gray-400" />
                    </div>
                    <p>
                      <span className="font-medium">Fiscal Year:</span>{' '}
                      {company?.fiscalYearStart ? 
                        new Date(0, company.fiscalYearStart - 1).toLocaleString('en', { month: 'long' }) 
                        : 'January'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Processing — only shows when Elavon gateway is active */}
              {merchantInfo && merchantInfo.gatewayType === 'elavon' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <p className="text-sm font-medium text-gray-600">Payment Processing</p>
                  </div>
                  <div className="pl-6 text-gray-900 leading-relaxed space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                        <CreditCard className="h-3 w-3 text-gray-400" />
                      </div>
                      <p>
                        <span className="font-medium">Merchant Alias:</span>{' '}
                        <span className="font-mono text-sm">{merchantInfo.merchantAlias}</span>
                      </p>
                    </div>
                    {merchantInfo.processorMerchantId && (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                          <CreditCard className="h-3 w-3 text-gray-400" />
                        </div>
                        <p>
                          <span className="font-medium">MID:</span>{' '}
                          <span className="font-mono text-sm">{merchantInfo.processorMerchantId}</span>
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck className="h-3 w-3 text-gray-400" />
                      </div>
                      <p>
                        <span className="font-medium">Status:</span>{' '}
                        {merchantInfo.status ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                            {merchantInfo.status}
                          </span>
                        ) : merchantInfo.error ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500"></span>
                            Unable to verify
                          </span>
                        ) : (
                          <span className="text-gray-500 italic">Unknown</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="h-3 w-3 text-gray-400" />
                      </div>
                      <p>
                        <span className="font-medium">Environment:</span>{' '}
                        <span className="text-sm">
                          {merchantInfo.environment === 'sandbox' ? 'Sandbox (UAT)' :
                           merchantInfo.environment === 'production_us' ? 'Production (US)' :
                           merchantInfo.environment === 'production_eu' ? 'Production (EU)' :
                           merchantInfo.environment}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

      {/* Company Settings Modal */}
      <CompanySettingsModal
        isOpen={isSettingsModalOpen}
        onClose={handleCloseSettings}
        company={company}
        onUpdateCompany={handleUpdateCompany}
        isUpdating={isUpdating}
        updateError={updateError}
        scrollToId={isMerchantAliasDeepLink(location.hash) ? MERCHANT_ALIAS_ANCHOR_ID : undefined}
      />
    </>
  );
};

export default CompanyDashboard;